import type { PhaseRecord, TimeOffBlock } from "@/lib/api";
import {
  type GanttProject,
  type Range,
  type StagedPlan,
  dayDiff,
  dayToX,
  dragBlock,
  monthDay,
  planOf,
  weeklyCounts,
} from "@/lib/gantt";
import { DOMAIN_LABEL } from "@/lib/overview";
import { css, cx } from "@styled-system/css";
import { useEffect, useRef } from "react";
import { MilestoneDiamond } from "./milestone-diamond";
import { DOMAIN_INK, PhaseBar } from "./phase-bar";
import { ChartWeekStrip } from "./week-strip";

export const ROW_H = 30;

const pane = css({
  position: "relative",
  overflowX: "auto",
  overflowY: "hidden",
  border: "default",
  borderRadius: "4",
  bg: "surface",
  minW: "0",
  maxW: "full",
});
const inner = css({ position: "relative" });
const overlay = css({
  position: "absolute",
  top: "0",
  bottom: "0",
  pointerEvents: "none",
  zIndex: "[0]",
});
const weekLine = css({
  position: "absolute",
  top: "0",
  bottom: "0",
  w: "[1px]",
  bg: "[var(--oks-colors-border)]",
  opacity: "0.6",
});
const todayRule = css({
  position: "absolute",
  top: "0",
  bottom: "0",
  w: "[2px]",
  bg: "text.accent.purple",
  opacity: "0.8",
});
const offBand = css({
  position: "absolute",
  top: "0",
  bottom: "0",
  bg: "[repeating-linear-gradient(45deg, var(--oks-colors-border) 0 1px, transparent 1px 6px)]",
  opacity: "0.9",
});
const offWord = css({
  position: "absolute",
  top: "[4px]",
  left: "[2px]",
  fontFamily: "mono",
  fontSize: "[10px]",
  lineHeight: "[12px]",
  color: "text.subtle",
});

const row = css({
  position: "relative",
  display: "flex",
  borderBottom: "[1px solid var(--oks-colors-border)]",
});
const rowProject = css({ bg: "surface.raised" });
const rowStrip = css({
  borderTop: "[1px solid var(--oks-colors-border-bold)]",
  borderBottom: "none",
});
const left = css({
  position: "sticky",
  left: "0",
  zIndex: "[5]",
  flexShrink: "0",
  display: "flex",
  alignItems: "center",
  gap: "6",
  px: "10",
  bg: "surface",
  borderRight: "[1px solid var(--oks-colors-border)]",
  overflow: "hidden",
  whiteSpace: "nowrap",
  fontSize: "[13px]",
  lineHeight: "[16px]",
  color: "text",
});
const leftProject = css({ bg: "surface.raised", fontWeight: "medium", color: "text.bold" });
const leftPhase = css({ pl: "20" });
const leftMuted = css({ color: "text.subtle", fontFamily: "mono", fontSize: "12" });
const nameText = css({ overflow: "hidden", textOverflow: "ellipsis", minW: "0" });
const count = css({
  fontFamily: "mono",
  fontSize: "12",
  color: "text.subtlest",
  ml: "auto",
  flexShrink: "0",
});
const dot = css({
  w: "[8px]",
  h: "[8px]",
  borderRadius: "999",
  bg: "[currentColor]",
  flexShrink: "0",
});
const track = css({ position: "relative", flexShrink: "0" });
const axisLabel = css({
  position: "absolute",
  top: "[50%]",
  transform: "translateY(-50%)",
  pl: "4",
  fontFamily: "mono",
  fontSize: "[11px]",
  color: "text.subtlest",
  whiteSpace: "nowrap",
});
const todayLabel = css({ color: "text.accent.purple" });

export interface GanttChartProps {
  groups: GanttProject[];
  /** Every phase in every phased project, for the weekly strip (the domain filter does not change it). */
  allPhases: PhaseRecord[];
  chart: Range & { days: number };
  weeks: string[];
  today: string;
  timeOff: TimeOffBlock[];
  dayW: number;
  leftW: number;
  narrow: boolean;
  staged: Record<string, StagedPlan>;
  draft: { id: string; range: Range } | null;
  pendingPlans: Record<string, { start: string; end: string; batch: string }>;
  selected: string | null;
  onDraft: (phaseId: string, r: Range | null) => void;
  onDrop: (phaseId: string, r: Range) => void;
  onSelect: (phaseId: string) => void;
}

/** Axis, week lines, the today rule, time-off shading, one row per phase, and the weekly strip. */
export function GanttChart(props: GanttChartProps) {
  const { groups, allPhases, chart, weeks, today, timeOff, dayW, leftW, narrow } = props;
  const { staged, draft, pendingPlans, selected, onDraft, onDrop, onSelect } = props;
  const trackW = chart.days * dayW;
  const x = (day: string) => dayToX(day, chart, dayW);
  const pendingIds = new Set(Object.keys(pendingPlans));
  const counts = weeklyCounts(allPhases, weeks);
  const inChart = (r: Range) => r.end >= chart.start && r.start <= chart.end;

  // Open the pane a week before today, so a narrow screen starts on the current work.
  const paneRef = useRef<HTMLElement>(null);
  const todayX = dayToX(today, chart, dayW);
  useEffect(() => {
    if (paneRef.current) paneRef.current.scrollLeft = Math.max(0, todayX - 7 * dayW);
  }, [todayX, dayW]);

  const leftCell = (content: React.ReactNode, extra?: string) => (
    <div className={cx(left, extra)} style={{ width: leftW, height: ROW_H }}>
      {content}
    </div>
  );

  return (
    <section ref={paneRef} className={pane} aria-label="Phase chart">
      <div className={inner} style={{ width: leftW + trackW }}>
        <div className={overlay} style={{ left: leftW, width: trackW }} aria-hidden="true">
          {weeks.map((w) => (
            <span key={w} className={weekLine} style={{ left: x(w) }} />
          ))}
          {timeOff.filter(inChart).map((b) => {
            const s = b.start < chart.start ? chart.start : b.start;
            const e = b.end > chart.end ? chart.end : b.end;
            return (
              <span
                key={b.id}
                className={offBand}
                style={{ left: x(s), width: (dayDiff(s, e) + 1) * dayW }}
                title={`Time off ${monthDay(b.start)} – ${monthDay(b.end)}`}
              >
                <span className={offWord}>off</span>
              </span>
            );
          })}
          {today >= chart.start && today <= chart.end && (
            <span className={todayRule} style={{ left: x(today) + dayW / 2 - 1 }} />
          )}
        </div>

        <div className={row}>
          {leftCell(<span className={leftMuted}>{narrow ? "" : "week of"}</span>)}
          <div className={track} style={{ width: trackW, height: ROW_H }}>
            {weeks.map((w) => (
              <span key={w} className={axisLabel} style={{ left: x(w) }}>
                {monthDay(w)}
              </span>
            ))}
            {today >= chart.start && today <= chart.end && (
              <span
                className={cx(axisLabel, todayLabel)}
                style={{ left: x(today) + dayW / 2, top: "85%" }}
              >
                today
              </span>
            )}
          </div>
        </div>

        {groups.map((g) => {
          const ink = DOMAIN_INK[g.project.domain];
          return (
            <section key={g.project.slug} aria-label={g.project.name}>
              <div className={cx(row, rowProject)}>
                {leftCell(
                  <>
                    <span className={cx(dot, ink)} aria-hidden="true" />
                    <span
                      className={nameText}
                      title={`${g.project.name} · ${DOMAIN_LABEL[g.project.domain]}`}
                    >
                      {g.project.name}
                    </span>
                    <span
                      className={count}
                      title={`${g.doneCount} of ${g.phases.length} phases done`}
                    >
                      {g.doneCount}/{g.phases.length}
                    </span>
                  </>,
                  leftProject
                )}
                <div className={track} style={{ width: trackW, height: ROW_H }} />
              </div>

              {g.unphased.length > 0 && (
                <div className={row}>
                  {leftCell(<span className={cx(nameText, leftMuted)}>Unphased</span>, leftPhase)}
                  <div className={track} style={{ width: trackW, height: ROW_H }}>
                    {g.unphased
                      .filter((m) => m.due >= chart.start && m.due <= chart.end)
                      .map((m) => (
                        <MilestoneDiamond
                          key={m.taskId}
                          text={m.text}
                          due={m.due}
                          open={m.open}
                          x={x(m.due) + dayW / 2}
                          outside={false}
                          ink={ink}
                        />
                      ))}
                  </div>
                </div>
              )}

              {g.phases.map((p) => {
                const plan = planOf(p);
                const st = staged[p.id];
                const pend = pendingPlans[p.id];
                return (
                  <div key={p.id} className={row}>
                    {leftCell(
                      <>
                        <span className={nameText} title={p.name}>
                          {p.name}
                        </span>
                        {!narrow && p.state === "done" && <span className={count}>done</span>}
                        {!narrow && st && <span className={count}>staged</span>}
                      </>,
                      leftPhase
                    )}
                    <div className={track} style={{ width: trackW, height: ROW_H }}>
                      <PhaseBar
                        phase={p}
                        domain={g.project.domain}
                        chart={chart}
                        dayW={dayW}
                        plan={plan}
                        staged={st ? st.to : null}
                        draft={draft?.id === p.id ? draft.range : null}
                        pending={pend ? { start: pend.start, end: pend.end } : null}
                        block={dragBlock(p, pendingIds)}
                        selected={selected === p.id}
                        onDraft={onDraft}
                        onDrop={onDrop}
                        onSelect={onSelect}
                      />
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })}

        <div className={cx(row, rowStrip)}>
          {leftCell(
            <span
              className={cx(nameText, leftMuted)}
              title="Active phases per week, all phased projects"
            >
              {narrow ? "Active/wk" : "Active phases / wk"}
            </span>
          )}
          <div className={track} style={{ width: trackW, height: ROW_H }}>
            <ChartWeekStrip counts={counts} xOf={x} weekW={7 * dayW} />
          </div>
        </div>
      </div>
    </section>
  );
}
